import { gql } from '@apollo/client';
import { FunctionWithFragments } from '@modernloop/shared/components';
import { v4 as uuid } from 'uuid';

import {
  FreeformSeatInput,
  GetSaveInterviewPlanInput_InterviewPlanFragment,
  JobStageInterviewInput,
  JobStageInterviewPlanUpsertInput,
  JobStageInterviewSeatEmployeeInput,
  JobStageInterviewSeatInput,
  LinkedSeatInput,
  ModuleSeatInput,
} from 'src/generated/mloop-graphql';

import InterviewPlan from '.';

type Fragments = {
  interviewPlan: GetSaveInterviewPlanInput_InterviewPlanFragment | null;
};

const getSaveInterviewPlanInput: FunctionWithFragments<
  Fragments,
  { finalJobStageId?: string; generateNewIds?: boolean; collaborationLinkEnabled?: boolean },
  JobStageInterviewPlanUpsertInput
> = ({ interviewPlan }, { finalJobStageId, generateNewIds = false, collaborationLinkEnabled }) => {
  const getId = (id: string): string => {
    return generateNewIds ? uuid() : id;
  };
  const jobStageId = finalJobStageId ?? uuid();

  const mapAttributes = (
    attributes:
      | ReadonlyArray<{ id?: string | null; attributeNameId?: string | null; attributeTagValueId?: string | null }>
      | null
      | undefined,
    seatId: string
  ) =>
    attributes?.map((attr) => ({
      id: attr.id,
      attributeNameId: attr.attributeNameId,
      attributeTagValueId: attr.attributeTagValueId,
      jobStageInterviewSeatId: seatId,
    }));

  if (!interviewPlan || !interviewPlan?.jobStageInterviewGroups) {
    return {
      jobStageId,
      groups: [],
      schedulingWindow: interviewPlan?.schedulingWindow?.seconds
        ? { seconds: interviewPlan?.schedulingWindow?.seconds }
        : undefined,
      excludedEmployeeIds: interviewPlan?.excludedEmployees?.map((employee) => employee.id),
    };
  }

  // Original id to new id mapping
  const linkSeatIdMap: {
    [linkedSeatId: string]: string;
  } = {};

  const groups = interviewPlan.jobStageInterviewGroups
    .map((group, groupIndex) => {
      const groupId = getId(group.id);
      return {
        id: groupId,
        locked: group.locked,
        jobStageId,
        index: groupIndex,
        name: group.name,
        jobStageInterviews: group?.jobStageInterviews?.map((interview, interviewIndex) => {
          const interviewId = getId(interview.id);

          const codingLinkInput = collaborationLinkEnabled
            ? {
                collaborationLinkTypes: interview.collaborationLinkTypes?.map((each) => {
                  return {
                    collaborationToolType: each.collaborationToolType,
                    collaborationLinkTemplateId: each.collaborationLinkTemplateId,
                  };
                }),
              }
            : { dynamicLinkTypes: interview.dynamicLinkTypes };

          return {
            id: interviewId,
            isLockedOrder: interview.isLockedOrder,
            jobStageId,
            index: interviewIndex,
            name: interview.name,
            interviewType: interview.interviewType,
            duration: interview.duration,
            isHiddenFromCandidate: interview.isHiddenFromCandidate,
            atsInterviewDefinitionId: interview.atsInterviewDefinition?.atsId,
            interviewGuideId: interview.interviewGuide?.id,
            jobStageInterviewGroupId: groupId,
            ...codingLinkInput,
            jobStageInterviewSeats: interview?.jobStageInterviewSeats?.map((seat, seatIndex) => {
              // Create an id for the other seat if it doesn't exist
              if (seat.linkedSeat) {
                const linkedSeatId = seat.linkedSeat.linkedJobStageInterviewSeatId;
                linkSeatIdMap[linkedSeatId] = linkSeatIdMap[linkedSeatId] || getId(linkedSeatId);
              }
              // Add this interview to the linked seat map
              linkSeatIdMap[seat.id] = linkSeatIdMap[seat.id] || getId(seat.id);
              const seatId = linkSeatIdMap[seat.id];

              return {
                id: seatId,
                index: seatIndex,
                jobStageInterviewId: interviewId,
                moduleSeat: seat.moduleSeat
                  ? ({
                      interviewModuleId: seat.moduleSeat.interviewModuleId,
                      // This PR writes both the legacy flat list and the per-role configs. An empty
                      // legacy list must fall through to the per-role ids, not short-circuit on ??.
                      selectedEmployeeIds: seat.moduleSeat.selectedEmployeeIds?.length
                        ? seat.moduleSeat.selectedEmployeeIds
                        : [
                            ...(seat.moduleSeat.trainedConfig?.selectedEmployeeIds ?? []),
                            ...(seat.moduleSeat.shadowConfig?.selectedEmployeeIds ?? []),
                            ...(seat.moduleSeat.reverseShadowConfig?.selectedEmployeeIds ?? []),
                          ],
                      // An empty legacy attributes array must fall through to the per-role trained
                      // attributes, not short-circuit on ?? ([] is not nullish).
                      jobStageInterviewSeatAttributes: seat.moduleSeat.jobStageInterviewSeatAttributes?.length
                        ? seat.moduleSeat.jobStageInterviewSeatAttributes.map((attr) => ({
                            id: attr.id,
                            attributeNameId: attr.attributeNameId,
                            attributeTagValueId: attr.attributeTagValueId,
                            jobStageInterviewSeatId: seatId,
                          }))
                        : mapAttributes(seat.moduleSeat.trainedConfig?.selectedAttributes, seatId),
                      trainedConfig: seat.moduleSeat.trainedConfig
                        ? {
                            selectedEmployeeIds: seat.moduleSeat.trainedConfig.selectedEmployeeIds,
                            selectedAttributes: mapAttributes(seat.moduleSeat.trainedConfig.selectedAttributes, seatId),
                          }
                        : undefined,
                      shadowConfig: seat.moduleSeat.shadowConfig
                        ? {
                            selectedEmployeeIds: seat.moduleSeat.shadowConfig.selectedEmployeeIds,
                            selectedAttributes: mapAttributes(seat.moduleSeat.shadowConfig.selectedAttributes, seatId),
                          }
                        : undefined,
                      reverseShadowConfig: seat.moduleSeat.reverseShadowConfig
                        ? {
                            selectedEmployeeIds: seat.moduleSeat.reverseShadowConfig.selectedEmployeeIds,
                            selectedAttributes: mapAttributes(
                              seat.moduleSeat.reverseShadowConfig.selectedAttributes,
                              seatId
                            ),
                          }
                        : undefined,
                      traineeRequirement: seat.moduleSeat.traineeRequirement,
                      traineeSchedulingStrategy: seat.moduleSeat.traineeSchedulingStrategy,
                    } as ModuleSeatInput)
                  : undefined,
                linkedSeat: seat.linkedSeat
                  ? ({
                      linkedJobStageInterviewSeatId: linkSeatIdMap[seat.linkedSeat.linkedJobStageInterviewSeatId],
                    } as LinkedSeatInput)
                  : undefined,
                freeformSeat: seat.freeformSeat
                  ? ({
                      jobStageInterviewSeatEmployees: seat.freeformSeat.jobStageInterviewSeatEmployees?.map(
                        (employee) =>
                          ({
                            employeeId: employee.employeeId,
                            jobStageInterviewSeatId: seatId,
                            preferenceLevel: employee.preferenceLevel,
                          }) as JobStageInterviewSeatEmployeeInput
                      ),
                    } as FreeformSeatInput)
                  : undefined,
              } as JobStageInterviewSeatInput;
            }),
          } as JobStageInterviewInput;
        }),
      };
    })
    // if there are no jobStageInterviews, don't include the group
    .filter((group) => group.jobStageInterviews?.length);

  const input: JobStageInterviewPlanUpsertInput = {
    jobStageId,
    groups,
    schedulingWindow: interviewPlan?.schedulingWindow?.seconds
      ? { seconds: interviewPlan.schedulingWindow?.seconds }
      : undefined,
    excludedEmployeeIds: interviewPlan.excludedEmployees?.map((employee) => employee.id),
  };

  return input;
};

getSaveInterviewPlanInput.fragments = {
  interviewPlan: gql`
    ${InterviewPlan.fragments.jobStage}
    fragment getSaveInterviewPlanInput_interviewPlan on JobStage {
      id
      ...InterviewPlan_jobStage
    }
  `,
};

export default getSaveInterviewPlanInput;
